<template>
  <!-- CRM replica of client StepPayment.vue (text-bearing parts only).
       Drives off the profile's payment label + payment-confirm text. The real
       step also has promo/price/schedule logic, which carries no profile text
       and is intentionally omitted from the preview. -->
  <div class="step-payment">
    <h2 class="step-title">{{ titleText }}</h2>
    <p class="step-subtitle">{{ subtitleText }}</p>

    <div class="payment-methods">
      <label class="payment-method payment-method--selected">
        <input type="radio" checked class="payment-method-radio" />
        <div class="payment-method-icon">🧾</div>
        <div class="payment-method-info">
          <span class="payment-method-name">{{ paymentLabel }}</span>
          <span class="payment-method-desc">{{ paymentDesc }}</span>
        </div>
      </label>
      <!-- Second payment method, shown only when Stripe is enabled for this profile. -->
      <label v-if="showStripe" class="payment-method">
        <input type="radio" class="payment-method-radio" />
        <div class="payment-method-icon">💳</div>
        <div class="payment-method-info">
          <span class="payment-method-name">{{ stripeLabel }}</span>
          <span class="payment-method-desc">{{ stripeDesc }}</span>
        </div>
      </label>
    </div>

    <!-- Price overview & schedule (payment_schedule box) -->
    <div v-if="showSchedule" class="price-overview">
      <div class="price-row"><span>{{ t('checkout_base_package', 'Reisepreis') }}</span><span>€ 2.490,00</span></div>
      <div class="price-row price-row--total"><span>{{ t('checkout_total', 'Gesamtbetrag') }}</span><span>€ 2.490,00</span></div>
      <div class="price-row price-row--sub"><span>{{ t('checkout_down_payment', 'Anzahlung') }}</span><span>€ 747,00</span></div>
      <div class="price-row price-row--sub"><span>{{ t('checkout_remaining', 'Restzahlung') }}</span><span>€ 1.743,00</span></div>
    </div>

    <div v-if="showPaymentConfirm && paymentConfirmHtml" class="payment-confirm-row">
      <span class="toggle" :style="{ '--toggle-on': brandColor }">
        <input type="checkbox" />
        <span class="toggle-slider"></span>
      </span>
      <div class="payment-confirm" v-html="paymentConfirmHtml"></div>
    </div>

    <div class="step-actions">
      <button class="checkout-btn checkout-btn--secondary">{{ backText }}</button>
      <button class="checkout-btn checkout-btn--primary" :style="{ backgroundColor: brandColor }">{{ bookText }}</button>
    </div>
  </div>
</template>

<script>
import previewLang from './previewLang';

export default {
  name: 'PaymentPreview',
  mixins: [previewLang],
  props: {
    // Plain short texts for this language (from profile `texts[lang]`).
    texts: { type: Object, default: () => ({}) },
    // Rendered HTML for rich fields (payment confirm), already getHTML()'d.
    rich: { type: Object, default: () => ({}) },
    lang: { type: String, default: 'de' },
    brandColor: { type: String, default: '#1a73e8' },
    boxVisibility: { type: Object, default: () => ({}) }
  },
  computed: {
    // payment_schedule defaults OFF (matches the global default); payment_confirm OFF too.
    showSchedule() { return this.boxVisibility.payment_schedule === true || this.boxVisibility.payment_schedule === 1; },
    showPaymentConfirm() { return this.boxVisibility.payment_confirm === true || this.boxVisibility.payment_confirm === 1; },
    showStripe() { return this.boxVisibility.stripe_enabled === true || this.boxVisibility.stripe_enabled === 1; },
    stripeLabel() { return this.t('checkout_stripe_label', 'Credit card'); },
    stripeDesc() { return this.t('checkout_stripe_desc', 'Pay securely by card now.'); },
    titleText() { return this.t('checkout_payment_title', 'Payment'); },
    subtitleText() { return this.t('checkout_payment_subtitle', 'Choose your payment method and complete the payment.'); },
    paymentLabel() {
      return (this.texts && this.texts.checkout_payment_label)
        || this.LF.checkout_pay_invoice
        || this.t('checkout_invoice_payment', 'Invoice');
    },
    paymentDesc() {
      return this.LF.checkout_pay_invoice_desc
        || this.t('checkout_invoice_desc', 'Pay by invoice after booking');
    },
    // Mirrors the client fallback chain (StepPayment): profile rich text, else
    // the locale's boomerang_payment_confirm.
    paymentConfirmHtml() { return (this.rich && this.rich.checkout_payment_confirm) || this.t('boomerang_payment_confirm', ''); },
    backText() { return this.t('checkout_back', 'Back'); },
    bookText() { return this.t('checkout_confirm_invoice', 'Confirm & Book'); }
  }
};
</script>

<style scoped>
.step-title { font-size: 20px; font-weight: 700; margin: 0 0 6px; }
.step-subtitle { font-size: 13px; color: #666; margin: 0 0 24px; }

.payment-methods { margin-bottom: 18px; }
.payment-method {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 16px;
}
.payment-method--selected { border-color: #1a73e8; background: #f5faff; }
.payment-method-radio { width: auto; }
.payment-method-icon { font-size: 22px; }
.payment-method-info { display: flex; flex-direction: column; }
.payment-method-name { font-size: 14px; font-weight: 600; color: #333; }
.payment-method-desc { font-size: 12px; color: #888; }

.payment-confirm { font-size: 13px; color: #555; line-height: 1.5; margin: 0; }

.price-overview { border: 1px solid #e8e8e8; border-radius: 8px; padding: 14px 16px; margin: 0 0 18px; }
.price-row { display: flex; justify-content: space-between; font-size: 13px; color: #444; padding: 4px 0; }
.price-row--total { font-weight: 700; border-top: 1px solid #eee; margin-top: 4px; padding-top: 8px; }
.price-row--sub { color: #888; font-size: 12px; }

.payment-confirm-row { display: flex; align-items: flex-start; gap: 8px; margin: 0 0 18px; }
.toggle { position: relative; display: inline-block; width: 38px; height: 22px; flex-shrink: 0; }
.toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-slider { position: absolute; inset: 0; background-color: #ccc; border-radius: 22px; }
.toggle-slider::before { content: ''; position: absolute; height: 16px; width: 16px; left: 3px; top: 3px; background-color: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.25); }

.step-actions { margin-top: 28px; display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.checkout-btn { padding: 10px 28px; border: none; border-radius: 4px; font-size: 14px; font-weight: 600; }
.checkout-btn--primary { color: #fff; }
.checkout-btn--secondary { background: #f0f0f0; color: #333; }

.payment-confirm :deep(p) { margin: 0 0 8px; }
</style>
